"use client";

import React from "react";
import Link from "next/link";
import ServiceTemplatePage from "@components/services/ServiceTemplatePage";
import SectionHead from "@components/services/SectionHead";
import AnswerCard from "@components/services/AnswerCard";
import KeyFactsBar from "@components/services/KeyFactsBar";
import PainPointsGrid from "@components/services/PainPointsGrid";
import WhyDbiCards from "@components/services/WhyDbiCards";
import ScopeGrid from "@components/services/ScopeGrid";
import DeliverablesBand from "@components/services/DeliverablesBand";
import ProcessSteps from "@components/services/ProcessSteps";
import PricingTiers from "@components/services/PricingTiers";
import CaseStudyCard from "@components/services/CaseStudyCard";
import TrustSignalsBand from "@components/services/TrustSignalsBand";
import StickyPriceBar from "@components/sections/StickyPriceBar";
import type { StickyPriceBarData } from "@components/sections/StickyPriceBar";

export interface PageData {
  hero: {
    badge: string;
    title: string;
    highlightedText: string;
    description: string;
    backgroundImage: string;
    photoAlt: string;
    tag: string;
    trustItems: string[];
  };
  answer: {
    question: string;
    paragraphs: string[];
    keyfacts?: { label: string; sublabel: string }[];
  };
  problem: {
    title: string;
    intro: string;
    items: { id: string; title: string; description: string }[];
  };
  usp: {
    title: string;
    answerParagraphs: string[];
    image: string;
    imageAlt: string;
    cards: { id: string; icon?: string; title: string; description: string }[];
  };
  services: {
    title: string;
    items: {
      iconName?:
        | "Search"
        | "Bot"
        | "FileText"
        | "Network"
        | "Database"
        | "Compass"
        | "RefreshCcw"
        | "Lightbulb";
      title: string;
      description: string;
    }[];
    deliverables?: { heading: string; items: string[] };
  };
  process: {
    title: string;
    badge: string;
    steps: { number: number; title: string; description: string }[];
  };
  /** Optional: omit the key to hide the pricing models entirely. */
  pricing?: {
    heading: string;
    layout: "grid" | "rows";
    tiers: {
      name: string;
      price: string;
      priceSuffix?: string;
      sub: string;
      features: string[];
      featured?: boolean;
    }[];
  };
  caseStudy: {
    tag: string;
    title: string;
    quote: string;
    image: string;
    imageAlt: string;
    metrics: { value: string; label: string }[];
    cta?: { label: string; href: string };
  };
  trustSignals: {
    items: { icon?: string; label: string; sublabel?: string }[];
  };
  faq: {
    faqs: { question: string; answer: string }[];
  };
  serviceSubnav: { id: string; label: string; section: string }[];
  relatedServices: {
    tag: string;
    title: string;
    description: string;
    href: string;
  }[];
  ctaBlock: {
    title: string;
    description: string;
    primaryCta: { label: string; href: string };
    secondaryCta?: { label: string; href: string };
  };
  stickyBar: StickyPriceBarData;
}

interface FabricBeratungClientProps {
  data: PageData;
}

export default function FabricBeratungClient({ data }: FabricBeratungClientProps) {
  const hero = {
    badge: data.hero.badge,
    title: data.hero.title,
    highlight: data.hero.highlightedText,
    highlightPosition: "after" as const,
    description: data.hero.description,
    leadMeta: data.hero.trustItems,
    photoSrc: data.hero.backgroundImage,
    photoAlt: data.hero.photoAlt,
    tag: data.hero.tag,
    primaryCta: {
      label: "Erstberatung anfragen",
      href: "/meet",
      variant: "primary" as const,
    },
    secondaryCta: {
      label: "So läuft die Analyse",
      href: "#weg",
      variant: "ghost-inverse" as const,
    },
  };

  const sectionByKey: Record<string, React.ReactNode> = {
    answer: (
      <>
        <SectionHead eyebrow="01 · Definition" title={data.answer.question} blue />
        <AnswerCard paragraphs={data.answer.paragraphs} />
        {/* `keyfacts` is optional here: the figures (0 €, interner Aufwand,
            Dauer) are already stated in the explainer under the hero, and
            repeating them two screens later only invited the two blocks to
            drift apart. */}
        <KeyFactsBar facts={data.answer.keyfacts} />
      </>
    ),
    problem: (
      <>
        <SectionHead
          eyebrow="02 · Das Problem"
          title={data.problem.title}
          lede={data.problem.intro}
          blue
        />
        <PainPointsGrid items={data.problem.items} />
      </>
    ),
    usp: (
      <>
        <SectionHead eyebrow="03 · Unser Ansatz" title={data.usp.title} blue />
        <AnswerCard paragraphs={data.usp.answerParagraphs} />
        <WhyDbiCards
          cards={data.usp.cards}
          image={data.usp.image}
          imageAlt={data.usp.imageAlt}
        />
      </>
    ),
    services: (
      <>
        <SectionHead eyebrow="04 · Leistungen" title={data.services.title} blue />
        <ScopeGrid items={data.services.items} />
        {data.services.deliverables ? (
          <DeliverablesBand
            heading={data.services.deliverables.heading}
            items={data.services.deliverables.items}
          />
        ) : null}
      </>
    ),
    process: (
      <>
        <SectionHead eyebrow="05 · Der Weg" title={data.process.title} blue />
        <ProcessSteps
          steps={data.process.steps}
          badge={data.process.badge}
          /* The questionnaire link belongs to step 01, where the questionnaire
             is described — not to the section as a whole. */
          stepAddons={{
            1: (
              <Link className="process-step__cta" href="/toolkit">
                Fragebogen selbst starten
                <span aria-hidden="true">→</span>
              </Link>
            ),
          }}
        />
        {data.pricing ? (
          <PricingTiers
            heading={data.pricing.heading}
            layout={data.pricing.layout}
            tiers={data.pricing.tiers}
          />
        ) : null}
      </>
    ),
    case: (
      <>
        <SectionHead eyebrow="06 · Referenzen" title="Warum Teams mit DBI planen" blue />
        <CaseStudyCard
          tag={data.caseStudy.tag}
          title={data.caseStudy.title}
          quote={data.caseStudy.quote}
          image={data.caseStudy.image}
          imageAlt={data.caseStudy.imageAlt}
          metrics={data.caseStudy.metrics}
          cta={data.caseStudy.cta}
        />
        <TrustSignalsBand items={data.trustSignals.items} />
      </>
    ),
  };

  const sections = data.serviceSubnav
    .filter((item) => item.section !== "faq")
    .map((item) => {
      const content = sectionByKey[item.section];
      if (!content) {
        throw new Error(
          `FabricBeratungClient: unsupported section key '${item.section}' in serviceSubnav.`,
        );
      }
      return { id: item.id, content };
    });

  return (
    <ServiceTemplatePage
      hero={hero}
      heroAddon={
        <>
          {/* The three steps were buried in section 05 — a visitor had to scroll
              past four sections to learn what "kostenfreie Bestandsanalyse"
              actually asks of them. Same data as the process section, so the two
              cannot drift apart. */}
          <section className="fabric-howto" aria-labelledby="fabric-howto-title">
            <div className="container">
              <div className="fabric-howto__head">
                <h2 className="fabric-howto__title" id="fabric-howto-title">
                  So läuft die kostenfreie Bestandsanalyse
                </h2>
                <span className="fabric-howto__badge">{data.process.badge}</span>
              </div>
              <ol className="fabric-howto__steps">
                {data.process.steps.map((step) => (
                  <li key={step.number}>
                    <span className="fabric-howto__num" aria-hidden="true">
                      {String(step.number).padStart(2, "0")}
                    </span>
                    <span className="fabric-howto__step-title">{step.title}</span>
                  </li>
                ))}
              </ol>
              <div className="fabric-howto__actions">
                <Link className="btn btn-primary" href="/toolkit">
                  Fragebogen selbst starten
                  <span aria-hidden="true">→</span>
                </Link>
                <Link className="btn btn-ghost-inverse" href="#weg">
                  Alle drei Schritte im Detail
                </Link>
              </div>
            </div>
          </section>
          <StickyPriceBar data={data.stickyBar} />
        </>
      }
      subnav={data.serviceSubnav.map((item) => ({ id: item.id, label: item.label }))}
      sections={sections}
      faqEyebrow="07 · Häufige Fragen"
      faqTitle="Häufige Fragen zur Microsoft Fabric Beratung"
      faqs={data.faq.faqs}
      relatedServices={data.relatedServices}
      ctaBlock={data.ctaBlock}
    />
  );
}
